import { lightTheme } from '../themes/muiTheme';
import { ThemeProvider } from '@mui/material';
import { QueryClient, Query } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import SnackbarProvider from '../components/Atoms/Snackbar/Snackbar';
import AnalyticsProvider from './analytics';
import dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import { DateRangeProvider } from '../features/dateRanges/DateRanges.context';
import { AuthProvider } from '../features/auth/providers/AuthProvider';
import { LocationProvider } from '../features/locations/Location.context';
import { QUERY_CACHE_KEY, DEFAULT_CACHE_TIME, DEFAULT_STALE_TIME } from '../utils/localStorage.utils';
dayjs.extend(utc.default);

// Providers configuration
interface ProviderConfig {
  component: React.JSXElementConstructor<any>;
  props?: any;
}



const providers: ProviderConfig[] = [
  {
    component: PersistQueryClientProvider,
    props: {
      client: new QueryClient({
        defaultOptions: {
          queries: {
            cacheTime: DEFAULT_CACHE_TIME,
            staleTime: DEFAULT_STALE_TIME,
          },
        },
      }),
      persistOptions: {
        persister: createSyncStoragePersister({
          storage: window.localStorage,
          key: QUERY_CACHE_KEY,
        }),
        dehydrateOptions: {
          shouldDehydrateQuery: (query: Query) => {
            return query.meta?.persist !== false;
          },
        },
        maxAge: DEFAULT_CACHE_TIME,
      },
    },
  },
  { component: AuthProvider },
  { component: AnalyticsProvider },
  {
    component: ThemeProvider,
    props: { theme: lightTheme },
  },
  {
    component: SnackbarProvider,
  },
  { component: DateRangeProvider },
  { component: LocationProvider },
];

const AppProviders: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const renderProviders = (currentChildren: React.ReactNode) => {
    return providers.reduceRight((kids, provider) => {
      const ProviderComponent = provider.component;
      return <ProviderComponent {...provider.props}>{kids}</ProviderComponent>;
    }, currentChildren);
  };

  return <>{renderProviders(children)}</>;
};

export default AppProviders;
